import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ContentTypeChip.tsx -- the per-message content-type selector that
// sits left of the send button on the chat composer.
//
// User spec (verbatim): "To the left of the send button on the chat
// have a box the same size as the send button but have it have a
// menu option open if clicked on with the default being text chat."
//
//   Auto         -> agent decides from the prompt (no bias)
//   Aa           -> Text Chat (default; just chat reply, no doc)
//   </>          -> Code (generate a code file via fdn_document_generate)
//   [camera]     -> Image (generate an image via fdn_document_generate)
//   [chart]      -> Spreadsheet (.xlsx via code-interpreter)
//   [page]       -> Document (.md long-form prose)
//
// State lives on chatStore.ChatWindow.nextMessageContentType (see
// chatStore.ts). The store resets it to 'text' after every send so
// follow-up turns aren't biased by the prior turn's chip.
//
// Layer: presentation only. Wire emission happens in chatStore.send
// where the captured contentType is forwarded to streamAgent ->
// /api/p/<projectId>/chat/sessions/<sid>/messages with `contentType`
// on the body. The BFF prepends a synthetic system instruction
// biasing the agent's tool-call choice.
//
// Per fdn-app's general styling convention: emoji-free output (per
// the user-level no-emoji output style). The chip uses single-glyph
// labels that aren't emojis: "Aa", "</>", monogram-style "[]" for
// the camera, etc. The selected option's glyph shows on the chip
// face; the menu shows glyph + label per option for discoverability.
import { useEffect, useRef, useState } from 'react';
import { useChatStore } from '../../state/chatStore';
// Order = display order in the menu. Default 'text' is second so the
// chip face shows "Aa" when the chat window first opens, matching the
// user spec ("default being text chat").
//
// Glyph choices per the user spec:
//   - Auto                = sparkles emoji (suggests "AI decides")
//   - Text chat (default) = "Aa" monogram (typographic, distinctive)
//   - Code                = "</>" textual (universally readable)
//   - Image               = camera emoji (per user spec)
//   - Spreadsheet         = chart emoji
//   - Document            = page emoji
//
// Why a mix of textual + emoji glyphs: text-style glyphs ("Aa", "</>")
// communicate the affordance more directly than an emoji would
// (a typographic Aa says "text" louder than any emoji); emoji-style
// glyphs (camera / chart / page) are the universal visual shorthand
// for those file types and were what the user explicitly asked for.
//
// The output-style "no emojis" guidance applies to code, docs, logs,
// and prose -- not to UI affordances where the icon IS the
// affordance. Confirmed with the user.
const OPTIONS = [
    {
        type: 'auto',
        glyph: '\u2728', // sparkles
        label: 'Auto',
        hint: 'Agent decides what to produce based on the prompt.',
    },
    {
        type: 'text',
        glyph: 'Aa',
        label: 'Text chat',
        hint: 'Plain chat reply. No document is generated. (default)',
    },
    {
        type: 'code',
        glyph: '</>',
        label: 'Code',
        hint: 'Generate a code file (.py, .ts, .sql, ...).',
    },
    {
        type: 'image',
        glyph: '\uD83D\uDCF7', // camera (U+1F4F7)
        label: 'Image',
        hint: 'Generate an image (.png).',
    },
    {
        type: 'spreadsheet',
        glyph: '\uD83D\uDCCA', // bar chart (U+1F4CA)
        label: 'Spreadsheet',
        hint: 'Generate an .xlsx via code-interpreter.',
    },
    {
        type: 'document',
        glyph: '\uD83D\uDCC4', // page-facing-up (U+1F4C4)
        label: 'Document',
        hint: 'Generate a long-form .md document.',
    },
];
const OPTION_BY_TYPE = OPTIONS.reduce((acc, opt) => {
    acc[opt.type] = opt;
    return acc;
}, {});
export function ContentTypeChip(props) {
    const { windowId, disabled } = props;
    const current = useChatStore((s) => s.windows[windowId]?.nextMessageContentType ?? 'text');
    const setCurrent = useChatStore((s) => s.setNextMessageContentType);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    // Close menu on outside-click + Escape. Per fdn-app convention --
    // mirrors how the recipient picker, hint chip, and other floating
    // UIs handle dismissal.
    useEffect(() => {
        if (!open)
            return;
        function onDocClick(e) {
            if (!wrapRef.current)
                return;
            if (!wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === 'Escape')
                setOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    const currentOpt = OPTION_BY_TYPE[current];
    return (_jsxs("div", { ref: wrapRef, className: "assistant-ctype-wrap", children: [_jsx("button", { type: "button", className: "assistant-ctype-btn", onClick: () => setOpen((v) => !v), disabled: disabled, title: `Content type: ${currentOpt.label}\n${currentOpt.hint}`, "aria-haspopup": "menu", "aria-expanded": open, children: currentOpt.glyph }), open && (_jsx("div", { className: "assistant-ctype-menu", role: "menu", children: OPTIONS.map((opt) => (_jsxs("button", { type: "button", className: `assistant-ctype-item${opt.type === current ? ' is-active' : ''}`, onClick: () => {
                        setCurrent(windowId, opt.type);
                        setOpen(false);
                    }, role: "menuitem", title: opt.hint, children: [_jsx("span", { className: "assistant-ctype-glyph", children: opt.glyph }), _jsx("span", { className: "assistant-ctype-label", children: opt.label })] }, opt.type))) }))] }));
}
